import { z } from 'zod';

import { Pilot } from './pilot.js';
import { AssetRef, HexColor, HttpUrl, PackageName, Uuid } from './primitives.js';

/**
 * Inline data URL for a tile icon — populated by the Builder's icon
 * uploader (browser-side base64). The runtime prefers this over `iconRef`
 * when present so admins can iterate on visuals without owning an asset
 * pipeline. Capped at ~1MB encoded to keep configs from ballooning.
 */
const TileIconDataUrl = z
  .string()
  .startsWith('data:image/', 'must be a data:image/* URL')
  .max(1_048_576, 'icon data URL exceeds 1MB');

const TileBase = z.object({
  id: Uuid,
  label: z.string().min(1).max(40),
  iconRef: AssetRef.optional(),
  iconDataUrl: TileIconDataUrl.optional(),
  /** When false, the tile renders the icon only — label is hidden. */
  showLabel: z.boolean().optional(),
  /**
   * Pilot (canary) targeting. When set AND the tenant is entitled to
   * `pilotRollout`, the tile renders only on devices the rule selects;
   * everyone else never sees it. Evaluated client-side — see
   * `runtime/pilot.ts`. Available on every tile type (folder children
   * included, since they extend this base).
   */
  pilot: Pilot.optional(),
});

/** Launches an Android app via package name. */
export const AppTile = TileBase.extend({
  type: z.literal('app'),
  packageName: PackageName,
});
export type AppTile = z.infer<typeof AppTile>;

/** Opens a URL in the launcher's WebView (default) or the device's
 *  default browser via Android Intent (when `openExternal: true`).
 *  External opening only takes effect on hosts that implement it
 *  (AndroidIntentAgentHost); browser hosts always navigate in-page. */
export const WebTile = TileBase.extend({
  type: z.literal('web'),
  // http(s) only — a `javascript:`/`data:`/`intent:` URL here would reach
  // window.location.href (or an Android intent) and execute in the launcher
  // origin. See HttpUrl.
  url: HttpUrl,
  /**
   * If true, kiosk hosts that have a way to open the URL outside the
   * launcher (e.g., Android Intent.ACTION_VIEW) should do so. Hosts
   * that can't (browser, MobiControl Lockdown WebView) treat this as
   * a no-op and navigate in-place — never refuse the activation.
   */
  openExternal: z.boolean().default(false),
  /**
   * Tab behavior when `openExternal` is true — controls Chrome's tab
   * reuse on the Android-intent path:
   *   'new'  → fresh Chrome tab (Browser.EXTRA_CREATE_NEW_TAB)
   *   'same' → reuse the launcher's existing Chrome tab
   *            (com.android.browser.application_id keyed to our package)
   * Ignored when `openExternal` is false (the in-WebView path always
   * navigates in place). Undefined is treated as 'new' by every host,
   * which matches the pre-existing FLAG_ACTIVITY_NEW_TASK behavior, so
   * old configs keep launching exactly as before.
   */
  externalTab: z.enum(['same', 'new']).optional(),
});
export type WebTile = z.infer<typeof WebTile>;

/** Empty grid cell. Used to position other tiles deliberately. */
export const SpacerTile = TileBase.extend({
  type: z.literal('spacer'),
});
export type SpacerTile = z.infer<typeof SpacerTile>;

/**
 * Horizontal-rule tile — a thin accent bar that spans the entire grid
 * row it occupies. Useful for separating logical groups of tiles
 * (e.g. "Operations" tiles above the rule, "Tools" below). Doesn't
 * extend TileBase because it has no icon and its label is purely for
 * screen readers — not displayed visually. Grid-only: not allowed in
 * folders, featuredTiles, or tools (enforced by refinements at each
 * consumer of the Tile union).
 */
export const DividerTile = z.object({
  id: Uuid,
  type: z.literal('divider'),
  /** Optional screen-reader label. Not painted. */
  label: z.string().max(40).optional(),
  /** Bar color. Defaults to the launcher's --ml-accent when unset. */
  color: HexColor.optional(),
  /** Bar height in CSS px. Clamped 2–16; defaults to 8. */
  height: z.number().int().min(2).max(16).optional(),
});
export type DividerTile = z.infer<typeof DividerTile>;

/**
 * Invokes a SOTI JS Agent API action. Pro tier (agentActions flag) at runtime.
 *
 * Cross-field rule: `scriptRef` is required when `action === 'runScript'`.
 * The rule is enforced on the union (`Tile`) so that the discriminator stays
 * clean and `Tile.parse` is the single boundary for this check.
 */
export const AgentActionTile = TileBase.extend({
  type: z.literal('agentAction'),
  action: z.enum(['reboot', 'clearAppData', 'runScript', 'syncNow']),
  scriptRef: AssetRef.optional(),
  /** Optional target package for actions that need one (e.g., clearAppData). */
  targetPackage: PackageName.optional(),
});
export type AgentActionTile = z.infer<typeof AgentActionTile>;

/**
 * Tile that can live inside a folder. Folders may contain any leaf tile
 * type (app, web, spacer, agentAction) but not other folders — flat
 * hierarchy by design, easier for admins to reason about, easier UX to
 * surface, no recursion bug surface.
 */
const NonFolderTile = z.discriminatedUnion('type', [AppTile, WebTile, SpacerTile, AgentActionTile]);
export type NonFolderTile = z.infer<typeof NonFolderTile>;

/**
 * Container tile — taps open a modal with the child tiles. Useful for
 * grouping admin/IT apps (Settings, file managers, OEM tools) so the
 * primary grid stays focused on what the end user actually needs.
 *
 * Min 1 / max 16 children. The runScript cross-field rule still
 * applies to children — agentAction children with `runScript` need
 * their `scriptRef`. The refinement is applied via the children
 * array's per-element schema, not the parent.
 */
export const FolderTile = TileBase.extend({
  type: z.literal('folder'),
  children: z
    .array(
      NonFolderTile.superRefine((t, ctx) => {
        if (t.type === 'agentAction' && t.action === 'runScript' && t.scriptRef === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "scriptRef is required when action is 'runScript'",
            path: ['scriptRef'],
          });
        }
      }),
    )
    .min(1)
    .max(16),
});
export type FolderTile = z.infer<typeof FolderTile>;

/**
 * The full tile discriminated union. Parsing through `Tile` enforces the
 * `runScript → scriptRef` rule from SCHEMA.md § "Cross-field validation"
 * for top-level tiles; the FolderTile schema enforces the same rule for
 * its children.
 *
 * DividerTile is included so `Profile.grid.tiles` accepts it; consumers
 * that don't want dividers (featuredTiles, folder.children, tools)
 * refine the array element to reject `type === 'divider'`.
 */
export const Tile = z
  .discriminatedUnion('type', [
    AppTile,
    WebTile,
    SpacerTile,
    AgentActionTile,
    FolderTile,
    DividerTile,
  ])
  .superRefine((t, ctx) => {
    if (t.type === 'agentAction' && t.action === 'runScript' && t.scriptRef === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "scriptRef is required when action is 'runScript'",
        path: ['scriptRef'],
      });
    }
  });
export type Tile = z.infer<typeof Tile>;
